import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { axiosInstance } from '@/App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Plus, Trash2, Settings as SettingsIcon, RefreshCw } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

const Settings = ({ user }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [settings, setSettings] = useState({
    image_aspect_ratio: '1:1',
    image_width: 800,
    background_color: 'white',
  });
  const [webhooks, setWebhooks] = useState([]);
  const [newWebhook, setNewWebhook] = useState({ url: '', event_types: [] });
  const [loading, setLoading] = useState(true);
  const [ghlConnection, setGhlConnection] = useState({ connected: false });
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    fetchSettings();
    fetchGHLConnection();
    if (user?.role === 'admin') {
      fetchWebhooks();
    }
    
    // Check for reconnect parameter - auto-disconnect if present
    if (searchParams.get('reconnect') === 'true') {
      handleAutoDisconnect();
    }
    
    // Check for connection success
    if (searchParams.get('connected') === 'true') {
      toast.success('Successfully connected to ExcaliburCRM!');
      setSearchParams({}); // Clear the query param
      fetchGHLConnection(); // Refresh connection status
      
      // Notify App component that connection succeeded
      window.dispatchEvent(new CustomEvent('ghl-connection-success'));
    }
    
    // Check for errors
    const error = searchParams.get('error');
    if (error) {
      toast.error(`Connection failed: ${error}`);
      setSearchParams({});
    }

    // Listen for messages from OAuth popup
    const handleMessage = (event) => {
      if (event.data.type === 'OAUTH_SUCCESS') {
        toast.success('Successfully connected to ExcaliburCRM!');
        fetchGHLConnection();
      } else if (event.data.type === 'OAUTH_ERROR') {
        toast.error(`Connection failed: ${event.data.error || 'Unknown error'}`);
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [user, searchParams, setSearchParams]);

  const fetchSettings = async () => {
    try {
      const response = await axiosInstance.get('/settings');
      setSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchWebhooks = async () => {
    try {
      const response = await axiosInstance.get('/webhooks');
      setWebhooks(response.data);
    } catch (error) {
      console.error('Failed to fetch webhooks:', error);
    }
  };

  const handleSaveSettings = async () => {
    try {
      await axiosInstance.put('/settings', settings);
      toast.success('Settings saved successfully');
    } catch (error) {
      toast.error('Failed to save settings');
    }
  };

  const handleAddWebhook = async (e) => {
    e.preventDefault();
    try {
      await axiosInstance.post('/webhooks', newWebhook);
      toast.success('Webhook added successfully');
      setNewWebhook({ url: '', event_types: [] });
      fetchWebhooks();
    } catch (error) {
      toast.error('Failed to add webhook');
    }
  };

  const handleDeleteWebhook = async (id) => {
    try {
      await axiosInstance.delete(`/webhooks/${id}`);
      toast.success('Webhook deleted successfully');
      fetchWebhooks();
    } catch (error) {
      toast.error('Failed to delete webhook');
    }
  };

  const fetchGHLConnection = async () => {
    try {
      const response = await axiosInstance.get('/integration/connection');
      setGhlConnection(response.data);
    } catch (error) {
      console.error('Failed to fetch connection:', error);
    }
  };

  const handleAutoDisconnect = async () => {
    try {
      await axiosInstance.delete('/integration/disconnect');
      setGhlConnection({ connected: false });
      toast.info('Connection removed. Please reconnect to ExcaliburCRM.');
      setSearchParams({}); // Clear the reconnect parameter
    } catch (error) {
      console.error('Failed to auto-disconnect:', error);
      toast.error('Failed to disconnect. Please try manually.');
    }
  };

  const handleConnectGHL = async () => {
    try {
      const response = await axiosInstance.get('/oauth/authorize');
      const authUrl = response.data.authorization_url;
      
      // Check if we're in an iframe
      const isInIframe = window.self !== window.top;
      
      if (isInIframe) {
        // Open in popup window when in iframe
        const width = 600;
        const height = 700;
        const left = (window.screen.width - width) / 2;
        const top = (window.screen.height - height) / 2;
        
        const popup = window.open(
          authUrl,
          'ExcaliburCRM Authorization',
          `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
        );
        
        if (!popup) {
          toast.error('Please allow popups to connect to ExcaliburCRM');
          return;
        }
        
        // Listen for when the popup closes (user completed or cancelled OAuth)
        const checkPopup = setInterval(() => {
          if (popup.closed) {
            clearInterval(checkPopup);
            // Refresh connection status after popup closes
            setTimeout(() => {
              fetchGHLConnection();
            }, 1000);
          }
        }, 500);
      } else {
        // Not in iframe - can redirect normally
        window.location.href = authUrl;
      }
    } catch (error) {
      toast.error('Failed to initiate connection');
    }
  };

  const handleDisconnectGHL = async () => {
    try {
      await axiosInstance.delete('/integration/connection');
      setGhlConnection({ connected: false });
      toast.success('Disconnected successfully');
    } catch (error) {
      toast.error('Failed to disconnect');
    }
  };

  const handleRefreshConnection = async () => {
    try {
      setSyncing(true);
      toast.info('Refreshing connection...');
      
      const response = await axiosInstance.post('/auth/refresh');
      
      if (response.data.ok) {
        toast.success('Connection refreshed successfully!');
        // Refresh the connection status
        await fetchGHLConnection();
      }
    } catch (error) {
      console.error('Refresh error:', error);
      
      if (error.response?.status === 401) {
        toast.error('Session expired. Please reconnect to ExcaliburCRM.');
        // Optionally auto-disconnect and prompt reconnect
        setGhlConnection({ connected: false });
      } else {
        toast.error(error.response?.data?.detail || 'Failed to refresh connection');
      }
    } finally {
      setSyncing(false);
    }
  };

  const handleSyncProducts = async () => {
    setSyncing(true);
    try {
      const response = await axiosInstance.post('/integration/sync-products');
      const { imported, exported, updated_local, updated_remote, failed } = response.data;
      
      // Build success message
      const messages = [];
      if (imported > 0) messages.push(`Imported ${imported} from GHL`);
      if (exported > 0) messages.push(`Exported ${exported} to GHL`);
      if (updated_local > 0) messages.push(`Updated ${updated_local} local`);
      if (updated_remote > 0) messages.push(`Updated ${updated_remote} in GHL`);
      
      if (messages.length > 0) {
        toast.success(`Sync complete! ${messages.join(', ')}`);
      } else {
        toast.success('Products are in sync!');
      }
      
      if (failed > 0) {
        toast.warning(`${failed} products failed to sync`);
      }
      
      fetchGHLConnection();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sync products');
    } finally {
      setSyncing(false);
    }
  };

  const handleEventTypeToggle = (eventType) => {
    setNewWebhook((prev) => {
      const eventTypes = prev.event_types.includes(eventType)
        ? prev.event_types.filter((e) => e !== eventType)
        : [...prev.event_types, eventType];
      return { ...prev, event_types: eventTypes };
    });
  };

  const isAdmin = user?.role === 'admin';

  return (
    <div data-testid="settings-page">
      <div className="page-header">
        <h1 className="page-title">Settings</h1>
        <p className="page-subtitle">Configure your application</p>
      </div>

      <Tabs defaultValue="gohighlevel" className="w-full">
        <TabsList className="mb-6">
          <TabsTrigger value="gohighlevel" data-testid="ghl-settings-tab">ExcaliburCRM</TabsTrigger>
        </TabsList>

        <TabsContent value="gohighlevel">
          <div className="card">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 bg-gradient-to-br from-blue-500 to-purple-500 rounded-xl">
                <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2L2 7v10c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V7l-10-5z"/>
                </svg>
              </div>
              <div>
                <h2 className="text-xl font-semibold">ExcaliburCRM Integration</h2>
                <p className="text-sm text-slate-500">Sync products to your ExcaliburCRM account</p>
              </div>
            </div>

            {ghlConnection.connected ? (
              <div className="space-y-6">
                {ghlConnection.sso_only ? (
                  <div className="p-4 bg-yellow-50 border-2 border-yellow-200 rounded-lg">
                    <div>
                      <p className="font-medium text-yellow-800 mb-2">⚠️ Connected via SSO - API Access Needed</p>
                      <div className="text-sm text-yellow-700 space-y-2 mb-4">
                        <p>Location ID: {ghlConnection.location_id}</p>
                        {ghlConnection.company_id && <p>Company ID: {ghlConnection.company_id}</p>}
                        <p className="mt-2">You're logged in via ExcaliburCRM, but to sync products you need to grant API permissions.</p>
                      </div>
                      <Button
                        onClick={handleConnectGHL}
                        data-testid="grant-api-access-button"
                        size="sm"
                      >
                        Grant API Access for Product Sync
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="p-4 bg-green-50 border-2 border-green-200 rounded-lg">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-medium text-green-800 mb-2">✓ Connected to ExcaliburCRM</p>
                        <div className="text-sm text-green-700 space-y-1">
                          <p>Location ID: {ghlConnection.location_id}</p>
                          {ghlConnection.company_id && <p>Company ID: {ghlConnection.company_id}</p>}
                          {ghlConnection.last_synced && (
                            <p>Last synced: {new Date(ghlConnection.last_synced).toLocaleString()}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleRefreshConnection}
                          disabled={syncing}
                          data-testid="refresh-ghl-button"
                          className="text-blue-600 hover:bg-blue-50"
                        >
                          <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
                          {syncing ? 'Refreshing...' : 'Refresh'}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleDisconnectGHL}
                          data-testid="disconnect-ghl-button"
                          className="text-red-600 hover:bg-red-50"
                        >
                          Disconnect
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

 
              </div>
            ) : (
              <div className="space-y-4">
                <div className="p-6 border-2 border-dashed border-slate-300 rounded-lg text-center">
                  <p className="text-slate-600 mb-4">
                    Connect your ExcaliburCRM account to sync products automatically
                  </p>
                  <Button onClick={handleConnectGHL} data-testid="connect-ghl-button">
                    Connect ExcaliburCRM
                  </Button>
                </div>

                <div className="p-4 bg-slate-50 rounded-lg">
                  <h4 className="font-medium mb-2">What you'll need:</h4>
                  <ul className="text-sm text-slate-600 space-y-1">
                    <li>• An ExcaliburCRM account with location access</li>
                    <li>• Permission to manage products in your location</li>
                    <li>• You'll be redirected to ExcaliburCRM to authorize access</li>
                  </ul>
                </div>
              </div>
            )}
          </div>
        </TabsContent>

 
      </Tabs>
    </div>
  );
};

export default Settings;
